import React from "react";

// Canonical TalentFora lockup — person mark + "TalentFora" in ONE svg.
//
// Geometry is copied verbatim from the design kit's single source of truth,
// design/assets/logos/logo-light.svg (and its logo-dark.svg twin), including the
// viewBox "130 605 820 140". The marketing site renders those same files via
// <img src="/logo-light.svg">, so keeping the viewBox and every path byte-identical
// is what makes the app and marketing lockups render pixel-for-pixel alike.
//
// Because the mark and the wordmark live in one coordinate space, the gap between
// them is part of the artwork and cannot drift. The old pattern — <Logo size={34} />
// beside <Wordmark height={32} /> with a hand-picked flex gap — is what let every
// surface invent its own spacing and mark-to-text ratio.
//
// Only fills are themeable. `tone` mirrors the two kit files:
//   tone="light" → ink #18211c text + ocean #1f7d91 mark   (light surfaces)
//   tone="dark"  → white text + white mark                 (dark surfaces)
// "Fora" and the head dot stay coral #e3704f in both, per the kit.
//
// Aspect ratio is 820:140, so width ≈ 5.857 × height. Size by `height`.

const TONES = {
  light: { text: "#18211c", mark: "#1f7d91" },
  dark:  { text: "#ffffff", mark: "#ffffff" },
};

const CORAL = "#e3704f";

export default function BrandLockup({
  height = 30,
  tone = "light",
  textColor,
  markColor,
  coralColor = CORAL,
  className = "",
  style = {},
}) {
  const base = TONES[tone] || TONES.light;
  const text = textColor || base.text;
  const mark = markColor || base.mark;

  return (
    <svg
      xmlns="http://www.w3.org/2000/svg"
      viewBox="130 605 820 140"
      role="img"
      aria-label="TalentFora"
      height={height}
      style={{ height: `${height}px`, width: "auto", display: "block", flexShrink: 0, ...style }}
      className={className}
    >
      {/* "Talent" */}
      <path fill={text} d="M564.85,738.81l.06-45.96c.01-8.8-4.77-14.48-13.08-15.5-6.01-.74-15.69,3.34-15.75,11.67l-.37,49.7h-21.59s.04-79.07.04-79.07l18.97-.37,2.05,8.47c8.74-10.5,19.58-11.58,32.2-8.1,7.78,2.14,18.58,11.49,18.68,23l.49,56.01-21.69.15Z"/>
      <polygon fill={text} points="292.67 738.69 270.43 738.67 270.47 650.63 243.04 650.46 243.03 630.72 319.67 630.72 319.67 650.47 292.65 650.59 292.67 738.69"/>
      <path fill={text} d="M644.3,719.79l.69,19.26c-7.07.21-14.23.44-21.05-.85-11.81-2.23-19.24-13.88-19.29-25.24l-.13-34.42-13.41-.11.04-17.97c3.44-1.28,8.66-2.84,10.68-4.71,2.3-2.14,2.69-8.63,2.97-14.02l21.05-.42.26,17.93,18.59.39.16,18.85-19.04.14.67,33.61c.12,5.99,8.14,10.81,17.81,7.56Z"/>
      <rect fill={text} x="396.49" y="628.93" width="21.49" height="109.84"/>
      <path fill={text} d="M449.51,705.02c.48,7.56,6.08,14.18,11.48,15.45,9.52,2.23,16.51,1.77,23.52-6.78,5.81,1.88,12.57,4.99,17.94,8.26-11.1,18.28-31.17,21.85-49.06,16.13-17.42-5.57-27.58-23.18-26.49-42.22,1.04-18.22,14.39-35.15,34.29-37.49,13.46-1.58,26.39,1.79,34.56,11.02,8.29,9.35,11.77,21.98,9.44,35.49l-55.68.15ZM483,688.94c-2.33-10.56-9.75-14.49-18.32-13.81-6.48.51-12.39,5.87-15.03,13.67l33.36.14Z"/>
      <path fill={text} d="M321.79,735.12c-7.61-4.95-9.68-14.45-8.11-22.76,1.24-6.57,7.53-15.35,16.32-17.19l32.74-6.87c1.74-5.23-3.64-11.48-9.13-12.1-9.59-1.08-15.65,2.33-20.68,11.15l-17.05-8.29c6.64-16.05,21.39-21.46,37.78-21.03,14.22.37,30.68,9.25,30.83,25.06l.55,55.58-17.94.3-3.41-6.43c-12.04,9.35-28.41,11.37-41.91,2.59ZM339.58,723.41c6.55,2.33,14.18.63,18.09-3.27s5.34-8.81,5.51-15.58l-21.79,4.19c-2.38.46-5.75,4.9-6.07,7.14-.27,1.87,2.23,6.8,4.26,7.52Z"/>
      {/* "Fora" */}
      <path fill={coralColor} d="M813.73,699.2c0,23.2-18.8,42-42,42s-42-18.8-42-42,18.8-42,42-42,42,18.8,42,42ZM784.18,717.32c7.57-7.03,9.04-16.73,7-24.37-2.98-11.15-11.98-16.62-22.19-15.3-9.28,1.2-15.99,8-17.69,19.31-1.1,7.33,3.72,18.18,11.49,22.51,4.75,2.65,17.41,1.56,21.39-2.15Z"/>
      <path fill={coralColor} d="M879.95,735.28c-8.12-5.12-9.53-16.83-7.37-24.81,1.68-6.21,8.76-14.38,17.19-16.02l30.49-5.94c1.88-1.7-.64-9.23-3.42-10.45-10.6-4.68-19.9-1.52-25.58,9.23l-17.15-8.11c6.65-16.23,21.2-21.69,37.81-21.27,13.84.36,30.54,9.19,30.7,24.8l.57,55.84-17.73.37c-1.33.03-2.6-4.51-2.81-7.12-10.83,9.65-28.34,12.54-42.71,3.48ZM897.31,723c8.3,4,20.62-.53,23.82-10.37.54-1.66,3.16-5.18,1.85-5.95-.99-.57-4.49-1.71-5.61-1.49l-16.67,3.33c-2.31.46-5.97,3.57-6.63,5.66s.97,7.72,3.25,8.81Z"/>
      <path fill={coralColor} d="M719.76,677.46c1.25,6.7,1.59,13.4,1.35,19.45l-44.06.1.37,41.36-21.57.21-.09-107.66,73.12-.31-.28,19.87-51.58-.08.02,26.9,42.73.16Z"/>
      <path fill={coralColor} d="M844.91,738.59l-21.05-.04.06-76.53c4.88-2.92,12.08-3.14,18.39-1.85l1.71,10.94c5.15-10.54,12.35-13.48,25.47-11.95l-.79,18.56c-9.85-1.45-22.87,2.52-23.02,12.07l-.77,48.81Z"/>
      {/* Mark: body + head */}
      <path fill={mark} d="M230.12,738.69l-80.68.11c-6.63,0-12.9-4.98-12.63-11.97.84-21.1,17.77-36.26,38.37-36.16l28.07.13c20.47.1,36.76,15.1,37.59,36.2.19,4.73-5.02,11.68-10.73,11.69Z"/>
      <circle fill={mark} cx="188.52" cy="658.72" r="23.55"/>
      {/* Mark: coral dot */}
      <circle fill={coralColor} cx="188.79" cy="620.71" r="11.91"/>
    </svg>
  );
}
